'use client';

import {
  getArtworkKindLabel,
  getDesignSideLabel,
  getPrintAreaForSide,
  getTextAlignOption,
  getTextFontOption,
  hasCustomDesign,
  isImageArtwork,
  sideHasDesign,
  type CustomDesignState,
  type CustomizerSide,
} from './customizer';

export type PendingArtworkFiles = Partial<Record<CustomizerSide, File | null>>;

interface FinalizeCustomizerDesignOptions {
  design?: CustomDesignState | null;
  pendingArtworkFiles?: PendingArtworkFiles;
  productTitle: string;
  selectedImageUrl?: string;
}

interface FinalizedCustomizerDesign {
  design?: CustomDesignState | null;
  previewUrl?: string;
}

interface UploadedFile {
  url: string;
  filename: string;
}

async function uploadCustomizerFile(file: File): Promise<UploadedFile> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch('/api/upload', {
    method: 'POST',
    body: formData,
  });
  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Upload failed');
  }

  return {
    url: data.url,
    filename: data.filename || file.name,
  };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load image: ${url}`));
    image.src = url;
  });
}

function isTemporaryArtworkUrl(url: string): boolean {
  return url.startsWith('blob:');
}

function drawObjectCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = width / height;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  let sourceX = 0;
  let sourceY = 0;

  if (sourceRatio > targetRatio) {
    sourceWidth = sourceHeight * targetRatio;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = sourceWidth / targetRatio;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  text.split('\n').forEach((sourceLine) => {
    const words = sourceLine.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      return;
    }

    let line = words[0];
    words.slice(1).forEach((word) => {
      const next = `${line} ${word}`;
      if (ctx.measureText(next).width <= maxWidth) {
        line = next;
      } else {
        lines.push(line);
        line = word;
      }
    });
    lines.push(line);
  });

  return lines;
}

async function createDesignPreviewFile(
  design: CustomDesignState,
  productTitle: string,
  selectedImageUrl: string
): Promise<File | null> {
  const side = design.activeSide;
  const sideDesign = design.sides[side];
  if (!sideHasDesign(sideDesign)) return null;

  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = 1200;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const productImage = await loadImage(selectedImageUrl);
  drawObjectCover(ctx, productImage, 0, 0, canvas.width, canvas.height);

  const profile = getPrintAreaForSide(design.profile, side);
  const area = {
    x: ((profile.centerX - profile.width / 2) / 100) * canvas.width,
    y: ((profile.centerY - profile.height / 2) / 100) * canvas.height,
    width: (profile.width / 100) * canvas.width,
    height: (profile.height / 100) * canvas.height,
  };

  ctx.save();
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.setLineDash([8, 8]);
  ctx.strokeRect(area.x, area.y, area.width, area.height);
  ctx.restore();

  if (sideDesign.artworkUrl) {
    const centerX = area.x + (sideDesign.x / 100) * area.width;
    const centerY = area.y + (sideDesign.y / 100) * area.height;
    const artworkWidth = (sideDesign.scale / 100) * area.width;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((sideDesign.rotation * Math.PI) / 180);

    if (sideDesign.artworkKind === 'image') {
      const artworkImage = await loadImage(sideDesign.artworkUrl);
      const ratio = artworkImage.naturalHeight / artworkImage.naturalWidth || 1;
      const artworkHeight = artworkWidth * ratio;
      ctx.drawImage(artworkImage, -artworkWidth / 2, -artworkHeight / 2, artworkWidth, artworkHeight);
    } else {
      const boxWidth = Math.max(180, artworkWidth);
      const boxHeight = 86;
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      ctx.strokeStyle = 'rgba(0,0,0,0.28)';
      ctx.lineWidth = 2;
      ctx.fillRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
      ctx.strokeRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
      ctx.fillStyle = '#111111';
      ctx.font = '700 20px Inter, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(getArtworkKindLabel(sideDesign), 0, -10);
      if (sideDesign.artworkFilename) {
        ctx.font = '500 14px Inter, Arial, sans-serif';
        ctx.fillText(sideDesign.artworkFilename.slice(0, 34), 0, 18);
      }
    }
    ctx.restore();
  }

  if (sideDesign.text.trim()) {
    const centerX = area.x + (sideDesign.textX / 100) * area.width;
    const centerY = area.y + (sideDesign.textY / 100) * area.height;
    const textFont = getTextFontOption(sideDesign.textFont);
    const textAlign = getTextAlignOption(sideDesign.textAlign);
    const fontSize = Math.max(18, sideDesign.textSize * 1.35);
    const maxTextWidth = area.width * 0.92;
    const lineHeight = fontSize * 1.16;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((sideDesign.textRotation * Math.PI) / 180);
    ctx.font = `${sideDesign.textItalic ? 'italic ' : ''}${sideDesign.textBold ? '700' : '400'} ${fontSize}px ${textFont.css}`;
    ctx.textAlign = textAlign.id as CanvasTextAlign;
    ctx.textBaseline = 'middle';
    ctx.fillStyle = sideDesign.textColor;
    ctx.lineWidth = sideDesign.textColor === '#ffffff' ? 2 : 0;
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';

    const lines = wrapText(ctx, sideDesign.text.trim(), maxTextWidth);
    const startY = -((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, index) => {
      const y = startY + index * lineHeight;
      if (sideDesign.textColor === '#ffffff') ctx.strokeText(line, 0, y);
      ctx.fillText(line, 0, y);
    });
    ctx.restore();
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      const safeTitle = productTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
      const sideLabel = getDesignSideLabel(design, side).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      resolve(new File([blob], `${safeTitle || 'oktshirt'}-${sideLabel}-preview.png`, { type: 'image/png' }));
    }, 'image/png', 0.92);
  });
}

export async function finalizeCustomizerDesign({
  design,
  pendingArtworkFiles,
  productTitle,
  selectedImageUrl,
}: FinalizeCustomizerDesignOptions): Promise<FinalizedCustomizerDesign> {
  if (!design || !hasCustomDesign(design)) return { design };

  let finalizedDesign: CustomDesignState = {
    ...design,
    sides: {
      front: { ...design.sides.front },
      back: { ...design.sides.back },
    },
  };

  for (const side of ['front', 'back'] as CustomizerSide[]) {
    const file = pendingArtworkFiles?.[side];
    if (!file || !sideHasDesign(finalizedDesign.sides[side])) continue;

    const uploaded = await uploadCustomizerFile(file);
    finalizedDesign = {
      ...finalizedDesign,
      sides: {
        ...finalizedDesign.sides,
        [side]: {
          ...finalizedDesign.sides[side],
          artworkUrl: uploaded.url,
          artworkFilename: uploaded.filename,
          artworkKind: isImageArtwork(uploaded.filename) ? 'image' : 'file',
        },
      },
    };
  }

  for (const side of ['front', 'back'] as CustomizerSide[]) {
    const sideDesign = finalizedDesign.sides[side];
    if (!sideDesign.artworkUrl || !isTemporaryArtworkUrl(sideDesign.artworkUrl) || pendingArtworkFiles?.[side]) continue;

    finalizedDesign = {
      ...finalizedDesign,
      sides: {
        ...finalizedDesign.sides,
        [side]: {
          ...sideDesign,
          artworkUrl: '',
          artworkFilename: '',
          artworkKind: '',
          artworkWarnings: [],
        },
      },
    };
  }

  if (!selectedImageUrl) return { design: finalizedDesign };

  try {
    const previewFile = await createDesignPreviewFile(finalizedDesign, productTitle, selectedImageUrl);
    if (!previewFile) return { design: finalizedDesign };
    const uploadedPreview = await uploadCustomizerFile(previewFile);
    return { design: finalizedDesign, previewUrl: uploadedPreview.url };
  } catch (error) {
    console.warn('Design preview generation failed:', error);
    return { design: finalizedDesign };
  }
}
